import type { JizdaListItem } from '@/api/types/Jizda';

// Semantic colour resolution for a calendar fůra row (CHR-03 §6 / ADR-0051).
// Green = assigned (has a dispatcher), red = unassigned, YELLOW = mine (the
// logged-in user's own assignment — my-jizdy highlight). STORNO wins for colour
// (always red) and adds strikethrough. Deleted fades the row (and is only
// rendered when the show-deleted filter is on — that gating lives in the host,
// not here). Kept as a pure descriptor (no theme dependency) so it is
// unit-testable; the component maps these tokens onto `theme.statusPalette`.
//
// Precedence (highest first): storno (red+strike) > mine (yellow) > assigned
// (green) / unassigned (red).

export type CalendarRowTone = 'assigned' | 'unassigned' | 'mine';

export interface CalendarRowStyle {
  // 'assigned' → statusPalette.done (green); 'unassigned' → statusPalette.paused
  // (red); 'mine' → statusPalette.mine (yellow). STORNO forces 'unassigned'
  // (red) regardless of assignment; 'mine' wins over plain 'assigned'.
  tone: CalendarRowTone;
  // STORNO → strikethrough the whole row.
  strikeThrough: boolean;
  // Soft-deleted → faded (opacity). Host only renders deleted rows when
  // show-deleted is on, but the fade descriptor lives here for one-place truth.
  faded: boolean;
}

// `mySub` is the logged-in user's Keycloak sub (or `null` when not loaded). The
// row is "mine" only on a pure sub-match with a non-null `mySub`.
export const resolveCalendarRowStyle = (
  item: JizdaListItem,
  mySub: string | null,
): CalendarRowStyle => {
  const isStorno = item.stornoState === 'STORNO';
  const isAssigned = item.assignedDispatcherSub !== null;
  const isMine = mySub !== null && item.assignedDispatcherSub === mySub;
  return {
    // Precedence: storno (red) > mine (yellow) > assigned (green) > unassigned
    // (red). Storno wins for colour even on a mine row.
    tone: isStorno
      ? 'unassigned'
      : isMine
        ? 'mine'
        : isAssigned
          ? 'assigned'
          : 'unassigned',
    strikeThrough: isStorno,
    faded: item.deleted,
  };
};
